import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { TrpcService } from '../trpc/trpc.service';

export interface Location {
  id: string;
  name: string;
  description?: string;
}

export interface RecentLocation {
  id: string;
  name: string;
  lastUsed: Date;
}

@Injectable({
  providedIn: 'root'
})
export class LocationSwitcherService {
  
  private currentLocationSubject = new BehaviorSubject<Location | null>(null);
  private recentLocationsSubject = new BehaviorSubject<RecentLocation[]>([]);
  private allLocationsSubject = new BehaviorSubject<Location[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  public currentLocation$: Observable<Location | null> = this.currentLocationSubject.asObservable();
  public recentLocations$: Observable<RecentLocation[]> = this.recentLocationsSubject.asObservable();
  public allLocations$: Observable<Location[]> = this.allLocationsSubject.asObservable();
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  constructor(private trpcService: TrpcService) {
    this.initializeService();
  }

  private async initializeService() {
    // Wait for tRPC to be ready
    await this.trpcService.trpcReady;
    
    // Subscribe to current location from tRPC service
    this.trpcService.currentLocationObservable.subscribe(async (locationId: string) => {
      if (locationId) {
        await this.updateCurrentLocationInfo(locationId);
      }
    });
    
    // Load initial data
    await this.loadAllLocations();
    await this.loadRecentLocations();
  }

  private async updateCurrentLocationInfo(locationId: string) {
    try {
      const locations = this.allLocationsSubject.value;
      const currentLocation = locations.find(loc => loc.id === locationId);
      
      if (currentLocation) {
        this.currentLocationSubject.next(currentLocation);
      } else {
        // If location not found in cached list, refresh the list
        await this.loadAllLocations();
        const updatedLocations = this.allLocationsSubject.value;
        const foundLocation = updatedLocations.find(loc => loc.id === locationId);
        this.currentLocationSubject.next(foundLocation || null);
      }
    } catch (error) {
      console.error('Error updating current location info:', error);
    }
  }

  private async loadAllLocations() {
    try {
      const locations: any = await this.trpcService.trpc.lots.listLocations.query();
      
      const mappedLocations: Location[] = (locations as any[]).map((loc: any) => ({
        id: loc.id?.toString() || '',
        name: loc.displayName || loc.name || `Location ${loc.id}`,
        description: `${loc.company} - ${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}`
      }));
      
      this.allLocationsSubject.next(mappedLocations);
    } catch (error) {
      console.error('Error loading all locations:', error);
      this.allLocationsSubject.next([]);
    }
  }

  private async loadRecentLocations() {
    try {
      // This will be implemented once we add the backend endpoint
      const recentLocationData: any = await this.trpcService.trpc.lots.getRecentLocations.query();
      const allLocations = this.allLocationsSubject.value;
      
      const recentLocations: RecentLocation[] = (recentLocationData as any[]).map((recent: any) => {
        const location = allLocations.find(loc => loc.id === recent.locationId);
        return {
          id: recent.locationId,
          name: location?.name || `Location ${recent.locationId}`,
          lastUsed: new Date(recent.lastUsed)
        };
      });
      
      this.recentLocationsSubject.next(recentLocations);
    } catch (error) {
      console.error('Error loading recent locations:', error);
      // For now, keep empty array until backend endpoint is implemented
      this.recentLocationsSubject.next([]);
    }
  }

  async switchToLocation(locationId: string): Promise<boolean> {
    this.isLoadingSubject.next(true);
    
    try {
      // Call the backend to switch location
      await this.trpcService.trpc.lots.switchLocation.mutate({ locationId });
      
      // Update current location immediately
      const location = this.allLocationsSubject.value.find(loc => loc.id === locationId);
      if (location) {
        this.currentLocationSubject.next(location);
        
        // Add to recent locations
        await this.addToRecentLocations(location);
      }
      
      // Trigger the observable update in tRPC service
      this.trpcService.currentLocationObservable.next(locationId);
      
      return true;
    } catch (error) {
      console.error('Error switching location:', error);
      return false;
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  private async addToRecentLocations(location: Location) {
    try {
      // Save to backend
      await this.trpcService.trpc.lots.saveRecentLocation.mutate({ 
        locationId: location.id 
      });
      
      // Update local state
      const currentRecent = this.recentLocationsSubject.value;
      const existingIndex = currentRecent.findIndex(recent => recent.id === location.id);
      
      const newRecentLocation: RecentLocation = {
        id: location.id,
        name: location.name,
        lastUsed: new Date()
      };
      
      let updatedRecent = [...currentRecent];
      
      // Remove if already exists
      if (existingIndex !== -1) {
        updatedRecent.splice(existingIndex, 1);
      }
      
      // Add to beginning
      updatedRecent.unshift(newRecentLocation);
      
      // Keep only last 5
      updatedRecent = updatedRecent.slice(0, 5);
      
      this.recentLocationsSubject.next(updatedRecent);
    } catch (error) {
      console.error('Error saving recent location:', error);
    }
  }

  async refreshLocations() {
    await this.loadAllLocations();
    await this.loadRecentLocations();
  }

  getCurrentLocation(): Location | null {
    return this.currentLocationSubject.value;
  }

  getRecentLocations(): RecentLocation[] {
    return this.recentLocationsSubject.value;
  }

  getAllLocations(): Location[] {
    return this.allLocationsSubject.value;
  }

  // Method to filter locations for autocomplete
  filterLocations(query: string): Location[] {
    const allLocations = this.allLocationsSubject.value;
    if (!query.trim()) {
      return allLocations;
    }
    
    const searchTerm = query.toLowerCase();
    return allLocations.filter(location =>
      location.name.toLowerCase().includes(searchTerm) ||
      location.id.toLowerCase().includes(searchTerm) ||
      (location.description && location.description.toLowerCase().includes(searchTerm))
    );
  }
}